import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";
import Reveal from "@/components/Reveal";
import { Minus, Plus, Flame, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Booking() {
  const { t, locale } = useLanguage();
  const [availability, setAvailability] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [guests, setGuests] = useState(2);
  const [form, setForm] = useState({ name: "", email: "", phone: "", dietary: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/availability`)
      .then((res) => {
        setAvailability(res.data.dates);
        const first = res.data.dates.find((d) => d.remaining > 0);
        if (first) setSelectedDate(first.date);
      })
      .catch(() => toast.error(t.booking.errorToast));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = availability.find((d) => d.date === selectedDate);
  const maxGuests = selected ? Math.min(10, selected.remaining) : 10;

  useEffect(() => {
    if (guests > maxGuests && maxGuests > 0) setGuests(maxGuests);
  }, [maxGuests, guests]);

  const fmtDate = (iso, style = "full") =>
    new Intl.DateTimeFormat(locale, style === "full"
      ? { weekday: "long", day: "numeric", month: "long" }
      : { weekday: "short", day: "numeric", month: "short" }).format(new Date(iso + "T12:00:00"));

  const total = useMemo(() => new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }).format(75 * guests), [guests, locale]);

  const submit = async () => {
    if (!form.name.trim() || !/.+@.+\..+/.test(form.email)) {
      toast.error(t.booking.fillToast);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/bookings`, {
        date: selectedDate,
        guests,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        dietary: form.dietary.trim(),
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || t.booking.errorToast);
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-night/70 border border-stroke rounded-xl px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-sunset transition-colors duration-300";

  return (
    <section id="booking" data-testid="booking-section" className="relative py-24 sm:py-32 bg-surface/40 border-y border-stroke/60 overflow-hidden">
      <div className="absolute -top-32 right-0 w-[28rem] h-[28rem] rounded-full bg-sunset/10 blur-[130px] pointer-events-none" />
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-ocean mb-4">{t.booking.kicker}</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-12">{t.booking.title}</h2>
        </Reveal>

        <div className="grid lg:grid-cols-[1fr_18rem] gap-10">
          <div className="space-y-10">
            <Reveal>
              <h3 className="font-mono text-xs uppercase tracking-widest text-slate-400 mb-4">{t.booking.step1}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="booking-date-select">
                {availability.map((d) => {
                  const active = d.date === selectedDate;
                  const out = d.remaining === 0;
                  return (
                    <button key={d.date} disabled={out} onClick={() => setSelectedDate(d.date)}
                      data-testid={`booking-date-card-${d.date}`}
                      className={`rounded-xl border p-3.5 text-left transition-all duration-300 ${active ? "border-sunset bg-sunset/10 shadow-[0_0_24px_rgba(255,94,26,0.2)]" : "border-stroke bg-night/60 hover:border-slate-500"} ${out ? "opacity-40 cursor-not-allowed" : ""}`}>
                      <span className="block text-sm font-semibold capitalize">{fmtDate(d.date, "short")}</span>
                      <span className={`block mt-1.5 text-[11px] font-mono uppercase tracking-wider ${out ? "text-red-400" : d.remaining <= 8 ? "text-flame" : "text-ocean"}`}>
                        {out ? t.booking.soldOut : `${d.remaining} ${t.booking.seatsLeft}`}
                      </span>
                    </button>
                  );
                })}
              </div>
            </Reveal>

            <Reveal delay={0.05}>
              <h3 className="font-mono text-xs uppercase tracking-widest text-slate-400 mb-4">{t.booking.step2}</h3>
              <div className="flex items-center gap-5">
                <button onClick={() => setGuests(Math.max(1, guests - 1))} data-testid="guest-count-decrement"
                  className="w-11 h-11 rounded-full border border-stroke bg-night/60 flex items-center justify-center hover:border-sunset hover:text-sunset transition-colors duration-300">
                  <Minus size={16} />
                </button>
                <div className="text-center min-w-[5rem]">
                  <span className="font-display text-4xl font-extrabold" data-testid="guest-count-display">{guests}</span>
                  <p className="text-[11px] font-mono uppercase tracking-widest text-slate-500">{t.booking.guests}</p>
                </div>
                <button onClick={() => setGuests(Math.min(maxGuests, guests + 1))} data-testid="guest-count-increment"
                  className="w-11 h-11 rounded-full border border-stroke bg-night/60 flex items-center justify-center hover:border-sunset hover:text-sunset transition-colors duration-300">
                  <Plus size={16} />
                </button>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <h3 className="font-mono text-xs uppercase tracking-widest text-slate-400 mb-4">{t.booking.step3}</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={t.booking.name} className={inputCls} data-testid="booking-name-input" />
                <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  type="email" placeholder={t.booking.email} className={inputCls} data-testid="booking-email-input" />
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder={t.booking.phone} className={inputCls} data-testid="booking-phone-input" />
                <input value={form.dietary} onChange={(e) => setForm({ ...form, dietary: e.target.value })}
                  placeholder={t.booking.dietary} className={inputCls} data-testid="booking-dietary-input" />
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div className="lg:sticky lg:top-24 rounded-2xl border border-stroke bg-night/70 p-6 space-y-4" data-testid="booking-summary">
              <div>
                <p className="text-[11px] font-mono uppercase tracking-widest text-slate-500">{t.booking.step1.split("· ")[1]}</p>
                <p className="font-semibold text-sm mt-1 capitalize" data-testid="booking-summary-date">
                  {selectedDate ? fmtDate(selectedDate) : "—"}
                </p>
              </div>
              <div className="flex justify-between text-sm text-slate-400">
                <span>{guests} × {t.booking.perPerson}</span>
              </div>
              <div className="border-t border-stroke pt-4 flex items-end justify-between">
                <span className="text-[11px] font-mono uppercase tracking-widest text-slate-500">{t.booking.total}</span>
                <span className="font-display text-3xl font-extrabold text-sunset" data-testid="total-price">{total}</span>
              </div>
              <button onClick={submit} disabled={loading || !selectedDate} data-testid="stripe-pay-button"
                className="w-full flex items-center justify-center gap-2 bg-sunset text-night font-bold py-3.5 rounded-full hover:bg-flame hover:scale-[1.02] active:scale-95 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? <><Loader2 size={16} className="animate-spin" /> {t.booking.processing}</> : <><Flame size={16} /> {t.booking.pay}</>}
              </button>
              <p className="text-[11px] text-slate-500 leading-relaxed">{t.booking.note}</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
